/**
 * The one place resolved feature-flag values live.
 *
 * Providers (JSON today, PostHog later) push raw flag maps in; the store merges
 * per-browser overrides on top and notifies subscribers. The composed `flags`
 * object keeps its reference identity until a value actually changes, because
 * `useSyncExternalStore` re-renders on every snapshot identity change.
 */
import { readOverrides } from './overrides'

const listeners = new Set()

const EMPTY = {}

let providerFlags = EMPTY
let snapshot = { flags: EMPTY, ready: false, source: 'none' }

const shallowEqual = (a, b) => {
  const keys = Object.keys(a)
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key])
}

const compose = () => {
  const overrides = readOverrides()
  const next = Object.keys(overrides).length ? { ...providerFlags, ...overrides } : providerFlags
  return shallowEqual(next, snapshot.flags) ? snapshot.flags : next
}

const publish = (ready, source) => {
  const flags = compose()
  if (flags === snapshot.flags && ready === snapshot.ready && source === snapshot.source) return

  snapshot = { flags, ready, source }
  listeners.forEach((listener) => listener())
}

export const getSnapshot = () => snapshot

export const getFlags = () => snapshot.flags

export const subscribe = (listener) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Called by the active provider whenever it has a new evaluation. */
export const setProviderFlags = (flags, source) => {
  providerFlags = flags || EMPTY
  publish(true, source || snapshot.source)
}

/** Re-applies overrides without touching provider state (after a manual override change). */
export const refresh = () => publish(snapshot.ready, snapshot.source)

export const reset = () => {
  providerFlags = EMPTY
  snapshot = { flags: EMPTY, ready: false, source: 'none' }
  listeners.forEach((listener) => listener())
}
